const tcaptchaScript = "https://turing.captcha.qcloud.com/TJCaptcha.js";
const cdnScript = "https://cdn.moguocdn.com/cdn/plugins/TJCaptcha.js";
import { uploadLiveLog } from "../utils/log";

// 全局单例加载 Promise，防止多次注入
let captchaLoadingPromise = null;

/**
 * 健壮的脚本加载器：支持检测已存在的标签、全局变量，并提供 Promise 封装
 */
const loadCaptchaScript = (url) => {
  if (captchaLoadingPromise) return captchaLoadingPromise;

  captchaLoadingPromise = new Promise((resolve, reject) => {
    // 1. 如果全局变量已存在，直接成功
    if (window.TencentCaptcha) {
      resolve();
      return;
    }

    // 2. 检查 DOM 中是否已有相同 URL 的脚本标签（可能是 main.js 或 index.html 注入的）
    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      // 如果脚本已存在但变量还没好，轮询等待其加载完成
      let pollCount = 0;
      const pollTimer = setInterval(() => {
        pollCount++;
        if (window.TencentCaptcha) {
          clearInterval(pollTimer);
          resolve();
        } else if (pollCount > 100) {
          // 10秒超时
          clearInterval(pollTimer);
          captchaLoadingPromise = null; // 失败后允许重试
          reject(new Error(`Captcha script (${url}) poll timeout`));
        }
      }, 100);
      return;
    }

    // 3. 真正需要动态注入的情况
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = url;
    script.async = true;
    script.onload = () => {
      resolve();
    };
    script.onerror = (e) => {
      captchaLoadingPromise = null; // 允许下次尝试其他 URL
      reject(e);
    };
    document.head.appendChild(script);
  });

  return captchaLoadingPromise;
};

export default {
  data() {
    return {
      captcha: null,
      showCaptchaLoading: false,
    };
  },
  mounted() {
    this.initCaptcha();
  },
  methods: {
    /**
     * 初始化验证码
     * 逻辑：优先检测全局变量 -> 轮询等待全局脚本 -> 动态注入主脚本 -> 动态注入备用脚本
     */
    async initCaptcha() {
      if (window.TencentCaptcha) {
        uploadLiveLog({
          message: "人机验证脚本-已经存在TencentCaptcha",
          module: "mixin",
          level: "info",
          tag: "pageLoad",
          extra: { component: "ManMachineVerify" },
        });
        this._createCaptchaInstance();
        return;
      }

      try {
        // 尝试等待或加载主脚本
        await loadCaptchaScript(tcaptchaScript);
        this._createCaptchaInstance();
        uploadLiveLog({
          message: "人机验证脚本-主脚本加载/获取成功",
          module: "mixin",
          level: "info",
          tag: "pageLoad",
          extra: { component: "ManMachineVerify" },
        });
      } catch (err) {
        uploadLiveLog(
          {
            message: "人机验证脚本-主脚本不可用，尝试备用 CDN",
            module: "mixin",
            level: "warn",
            tag: "pageLoad",
            extra: { component: "ManMachineVerify", error: err.message },
          },
          true
        );

        try {
          // 主脚本失败，清除 promise 尝试加载备用 CDN
          captchaLoadingPromise = null;
          await loadCaptchaScript(cdnScript);
          this._createCaptchaInstance();
        } catch (err2) {
          uploadLiveLog(
            {
              message: "人机验证脚本-所有加载渠道均失败",
              module: "mixin",
              level: "error",
              tag: "pageLoad",
              extra: { component: "ManMachineVerify", error: err2.message },
            },
            true
          );
        }
      }
    },

    _createCaptchaInstance() {
      if (window.TencentCaptcha && !this.captcha) {
        try {
          this.captcha = new window.TencentCaptcha(
            "194976309",
            this._captchaCallback,
            {}
          );
        } catch (error) {
          console.error("[ManMachineVerify] 实例化失败:", error);
        }
      }
    },

    _captchaCallback(res) {
      this.showCaptchaLoading = false;
      uploadLiveLog({
        message: "人机验证脚本-回调",
        module: "mixin",
        level: "info",
        tag: "api",
        extra: {
          component: "ManMachineVerify",
          res: res,
        },
      });
      if (res === "noCaptcha") {
        this.$confirm("人机验证失败，请刷新页面重试", "提示", {
          confirmButtonText: "确 定",
          cancelButtonText: "取 消",
          customClass: "saas-confirm-message",
        }).then(() => {
          location.reload();
        });
        return;
      }
      if (res.ret === 0) {
        if (res.errorCode) {
          this.$confirm("人机验证失败，请刷新页面重试", "提示", {
            confirmButtonText: "确 定",
            cancelButtonText: "取 消",
            customClass: "saas-confirm-message",
          }).then(() => {
            location.reload();
          });
        } else {
          this.captchaCallback(res);
        }
      }
    },

    async reloadCaptcha() {
      this.captcha = null;
      await this.initCaptcha();
    },

    /**
     * 显示验证码，具备自动补救加载逻辑
     */
    async showCaptcha() {
      try {
        this.showCaptchaLoading = true;

        console.log("showCaptcha", window.TencentCaptcha);
        if (!this.captcha) {
          await this.initCaptcha();
        }

        if (this.captcha) {
          this.captcha.show();
          return true;
        }

        uploadLiveLog(
          {
            message: "人机验证脚本-展示失败，无可用实例",
            module: "mixin",
            level: "error",
            tag: "api",
            extra: { component: "ManMachineVerify" },
          },
          true
        );
        this._captchaCallback("noCaptcha");
        return false;
      } catch (error) {
        this.showCaptchaLoading = false;
      }
    },
  },
};
